import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ArchiveOutlined,
  ChatBubbleOutline,
  Clear,
  DeleteOutline,
  Group,
  MoreVert,
  Search,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { formatTimeAgo } from '../../utils/dateUtils';

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'care_team', label: 'Care Circle' },
];

const isDefaultCareTeamConversation = (conversation = {}) => (
  conversation.conversationType === 'child_care_team'
  || /care team/i.test(conversation.title || '')
);

const formatConversationTimestamp = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  const now = new Date();
  const diffMinutes = Math.max(0, Math.round((now - date) / 60000));

  if (diffMinutes < 60) {
    return formatTimeAgo(date);
  }

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
};

const formatPreview = (conversation, currentUserId) => {
  const lastMessage = conversation.lastMessage;
  if (!lastMessage) {
    return 'No messages yet';
  }

  const previewText = lastMessage.text || `${lastMessage.type || 'Message'} shared`;
  if (lastMessage.senderId && lastMessage.senderId !== currentUserId && lastMessage.senderName) {
    return `${lastMessage.senderName}: ${previewText}`;
  }

  return previewText;
};

const getConversationContext = (conversation, childNameMap) => {
  const childName = childNameMap.get(conversation.childId) || conversation.childName || null;
  const memberCount = conversation.participants?.length || 0;
  if (childName && memberCount > 0) {
    return `${childName} • ${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
  }
  if (childName) {
    return childName;
  }
  if (memberCount > 0) {
    return `${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
  }
  return 'Care circle conversation';
};

const getAvatarLabel = (conversation, childNameMap) => {
  const childName = childNameMap.get(conversation.childId) || conversation.childName || '';
  return childName?.charAt(0)?.toUpperCase() || conversation.title?.charAt(0)?.toUpperCase() || 'C';
};

const ConversationRow = ({
  conversation,
  currentUserId,
  selected,
  onClick,
  childNameMap,
  onDeactivateConversation,
}) => {
  const unreadCount = conversation.unreadCounts?.[currentUserId] || 0;
  const timestamp = formatConversationTimestamp(
    conversation.lastMessage?.timestamp || conversation.updatedAt || conversation.createdAt
  );
  const preview = formatPreview(conversation, currentUserId);
  const contextText = getConversationContext(conversation, childNameMap);
  const isDefaultConversation = isDefaultCareTeamConversation(conversation);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    event?.stopPropagation?.();
    if (!actionLoading) {
      setMenuAnchorEl(null);
    }
  };

  const handleDeactivate = async (event) => {
    event.stopPropagation();
    if (!onDeactivateConversation) {
      return;
    }

    setActionLoading(true);
    await onDeactivateConversation(conversation);
    setActionLoading(false);
    setMenuAnchorEl(null);
  };

  return (
    <>
      <ListItemButton
        onClick={() => onClick(conversation)}
        selected={selected}
        aria-label={`${conversation.title || 'Untitled conversation'}. ${unreadCount > 0 ? `${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}` : 'No unread messages'}`}
        sx={{
          px: 2,
          py: 1.6,
          alignItems: 'flex-start',
          borderRadius: 3,
          border: '1px solid',
          borderColor: selected ? 'rgba(156, 216, 207, 0.56)' : 'rgba(226, 232, 240, 0.95)',
          bgcolor: selected ? 'rgba(245, 252, 250, 0.98)' : '#FFFFFF',
          boxShadow: selected ? '0 8px 20px rgba(15, 23, 42, 0.05)' : '0 4px 12px rgba(15, 23, 42, 0.03)',
          '&:hover': {
            bgcolor: selected ? 'rgba(245, 252, 250, 0.98)' : 'rgba(251, 253, 252, 1)',
            borderColor: 'rgba(196, 225, 218, 0.95)',
          },
        }}
      >
        <Badge
          overlap="circular"
          badgeContent={unreadCount > 0 ? unreadCount : null}
          sx={{
            mr: 1.5,
            '& .MuiBadge-badge': {
              minWidth: 20,
              height: 20,
              px: 0.6,
              borderRadius: 999,
              bgcolor: '#2FA39A',
              color: '#FFFFFF',
              fontWeight: 800,
              fontSize: '0.72rem',
              boxShadow: '0 4px 10px rgba(47, 163, 154, 0.22)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: '#AEE1D4',
              color: colors.landing.heroText,
              fontWeight: 800,
              fontSize: '1.15rem',
            }}
          >
            {isDefaultCareTeamConversation(conversation) ? <Group fontSize="small" /> : getAvatarLabel(conversation, childNameMap)}
          </Avatar>
        </Badge>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
            <Typography
              sx={{
                flex: 1,
                fontSize: '1.02rem',
                fontWeight: unreadCount > 0 ? 800 : 700,
                color: colors.landing.deepNavy,
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {conversation.title || 'Untitled conversation'}
            </Typography>
            <Typography
              sx={{
                flexShrink: 0,
                mt: 0.1,
                fontSize: '0.76rem',
                fontWeight: unreadCount > 0 ? 700 : 500,
                color: unreadCount > 0 ? '#2F7A74' : '#8A97A8',
              }}
            >
              {timestamp}
            </Typography>
            {onDeactivateConversation ? (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  ml: 0.25,
                  mt: -0.35,
                  color: '#8A97A8',
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            ) : null}
          </Box>

          <Typography
            sx={{
              mt: 0.45,
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#6E7F8F',
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {contextText}
          </Typography>

          <Typography
            sx={{
              mt: 0.55,
              fontSize: '0.92rem',
              color: unreadCount > 0 ? '#425166' : '#7F8A9C',
              fontWeight: unreadCount > 0 ? 600 : 400,
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {preview}
          </Typography>
        </Box>
      </ListItemButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDeactivate} disabled={actionLoading}>
          {isDefaultConversation ? <ArchiveOutlined sx={{ mr: 1.25, fontSize: 18 }} /> : <DeleteOutline sx={{ mr: 1.25, fontSize: 18 }} />}
          {isDefaultConversation ? 'Archive conversation' : 'Delete conversation'}
        </MenuItem>
      </Menu>
    </>
  );
};

const EmptyConversationsState = ({ searchQuery, canStartConversation, onNewConversation }) => (
  <Box
    sx={{
      px: 3,
      py: 8,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      color: '#6F7C90',
    }}
  >
    <Avatar
      sx={{
        width: 68,
        height: 68,
        bgcolor: '#EFF8F6',
        color: '#52B8AD',
        mb: 2,
      }}
    >
      <ChatBubbleOutline sx={{ fontSize: 34 }} />
    </Avatar>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: colors.landing.deepNavy }}>
      {searchQuery ? 'No conversations found' : 'No conversations yet'}
    </Typography>
    <Typography sx={{ mt: 1, maxWidth: 320, fontSize: '0.95rem', lineHeight: 1.55 }}>
      {searchQuery
        ? 'Try a different search term to find the conversation you need.'
        : 'Start a care circle chat to coordinate updates, questions, and important moments.'}
    </Typography>
    {!searchQuery && canStartConversation ? (
      <Button
        variant="contained"
        onClick={onNewConversation}
        sx={{
          mt: 2.5,
          borderRadius: 999,
          px: 2.8,
          py: 1.1,
          textTransform: 'none',
          fontWeight: 800,
          bgcolor: '#2FA39A',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#278D86', boxShadow: 'none' },
        }}
      >
        Start new chat
      </Button>
    ) : null}
  </Box>
);

const ConversationList = ({
  conversations = [],
  selectedConversation,
  onConversationSelect,
  onNewConversation = null,
  onDeactivateConversation = null,
  currentUserId,
  isMobile = false,
  loading = false,
  canStartConversation = true,
  children = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const childNameMap = useMemo(
    () => new Map((children || []).map((child) => [child.id, child.name])),
    [children]
  );

  const filteredConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const nextConversations = [...conversations].filter((conversation) => {
      const preview = formatPreview(conversation, currentUserId).toLowerCase();
      const childName = (childNameMap.get(conversation.childId) || '').toLowerCase();
      const participantCount = conversation.participants?.length || 0;
      const matchesSearch = !query
        || (conversation.title || '').toLowerCase().includes(query)
        || childName.includes(query)
        || preview.includes(query)
        || participantCount.toString() === query;

      if (!matchesSearch) {
        return false;
      }

      if (filterType === 'unread') {
        return (conversation.unreadCounts?.[currentUserId] || 0) > 0;
      }

      if (filterType === 'care_team') {
        return isDefaultCareTeamConversation(conversation);
      }

      return true;
    });

    nextConversations.sort((a, b) => {
      const aTime = new Date(a.lastMessage?.timestamp || a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.lastMessage?.timestamp || b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });

    return nextConversations;
  }, [childNameMap, conversations, currentUserId, filterType, searchQuery]);

  const totalUnreadCount = useMemo(
    () => conversations.reduce((sum, conversation) => sum + (conversation.unreadCounts?.[currentUserId] || 0), 0),
    [conversations, currentUserId]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <Box
        sx={{
          px: isMobile ? 2 : 3,
          pt: isMobile ? 2 : 3,
          pb: isMobile ? 1.5 : 2.25,
          borderBottom: '1px solid',
          borderColor: 'rgba(226, 232, 240, 0.9)',
          background: isMobile ? 'linear-gradient(180deg, #FCFFFE 0%, #F8FBFA 100%)' : 'rgba(252, 253, 255, 0.96)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
          <Box>
            <Typography
              sx={{
                fontSize: isMobile ? '2rem' : '1.75rem',
                fontWeight: 900,
                lineHeight: 1.05,
                color: colors.landing.deepNavy,
              }}
            >
              Conversations
            </Typography>
            {!isMobile && totalUnreadCount > 0 ? (
              <Typography sx={{ mt: 0.55, fontSize: '0.92rem', color: '#6F7C90' }}>
                {totalUnreadCount} unread {totalUnreadCount === 1 ? 'message' : 'messages'}
              </Typography>
            ) : null}
          </Box>

          {!isMobile && onNewConversation ? (
            <Button
              variant="contained"
              onClick={onNewConversation}
              sx={{
                borderRadius: 999,
                px: 2.3,
                py: 1,
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: '#2FA39A',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#278D86', boxShadow: 'none' },
              }}
            >
              New chat
            </Button>
          ) : null}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations…"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#7A8395' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: isMobile ? 56 : 52,
              borderRadius: 999,
              bgcolor: '#FFFFFF',
              '& fieldset': {
                borderColor: 'rgba(226, 232, 240, 0.95)',
              },
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, overflowX: 'auto', pb: 0.25 }}>
          {FILTER_OPTIONS.map((option) => (
            <Chip
              key={option.key}
              label={option.label}
              clickable
              onClick={() => setFilterType(option.key)}
              variant={filterType === option.key ? 'filled' : 'outlined'}
              sx={{
                borderRadius: 999,
                fontWeight: 800,
                bgcolor: filterType === option.key ? '#E8F6F3' : '#FFFFFF',
                color: filterType === option.key ? '#247E77' : '#516071',
                borderColor: filterType === option.key ? '#B7E4DB' : 'rgba(226, 232, 240, 0.95)',
                flexShrink: 0,
              }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: isMobile ? 1.5 : 1.75,
          py: 1.5,
          pb: isMobile && onNewConversation ? 11 : 1.5,
          bgcolor: isMobile ? '#F8FBFA' : '#FFFFFF',
        }}
      >
        {loading ? (
          <List sx={{ display: 'grid', gap: 1.25, p: 0 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  height: 92,
                  borderRadius: 3,
                  border: '1px solid rgba(226, 232, 240, 0.95)',
                  bgcolor: '#FFFFFF',
                }}
              />
            ))}
          </List>
        ) : filteredConversations.length === 0 ? (
          <EmptyConversationsState
            searchQuery={searchQuery}
            canStartConversation={canStartConversation}
            onNewConversation={onNewConversation}
          />
        ) : (
          <List sx={{ display: 'grid', gap: 1.15, p: 0 }}>
            {filteredConversations.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                currentUserId={currentUserId}
                selected={selectedConversation?.id === conversation.id}
                onClick={onConversationSelect}
                childNameMap={childNameMap}
                onDeactivateConversation={onDeactivateConversation}
              />
            ))}
          </List>
        )}
      </Box>

      {isMobile && onNewConversation ? (
        <Fab
          color="primary"
          aria-label="Start new chat"
          onClick={onNewConversation}
          sx={{
            position: 'absolute',
            right: 20,
            bottom: 20,
            width: 62,
            height: 62,
            bgcolor: '#2FA39A',
            boxShadow: '0 14px 30px rgba(47, 163, 154, 0.26)',
            '&:hover': {
              bgcolor: '#278D86',
            },
          }}
        >
          <Add />
        </Fab>
      ) : null}
    </Box>
  );
};

export default ConversationList;
